import { useMemo, useState } from 'react';
import { useCustom } from '@refinedev/core';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { format, parseISO, subDays } from 'date-fns';
import { ru } from 'date-fns/locale';
import { notify } from '@/lib/notify';
import { TrendingUp, CreditCard, Clock, Percent, AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import type { PaymentAnalyticsResponse } from '../analytics.types';

const PROVIDER_LABELS: Record<string, string> = {
  MANUAL: 'Ручной',
  CLICK: 'Click',
  PAYME: 'Payme',
  STRIPE: 'Stripe',
  MULTICARD: 'Multicard',
};

const TARGET_TYPE_LABELS: Record<string, string> = {
  COURSE: 'Курсы',
  SUBSCRIPTION_PLAN: 'Абонементы',
  CHANNEL_PASS: 'Пропуски',
  EVENT: 'Мероприятия',
};

const PROVIDER_COLORS: Record<string, string> = {
  MANUAL: '#6b7280',
  CLICK: '#3b82f6',
  PAYME: '#06b6d4',
  STRIPE: '#8b5cf6',
  MULTICARD: '#10b981',
};

interface DateRange {
  from?: Date;
  to?: Date;
}

function getDefaultRange(): DateRange {
  const to = new Date();
  const from = subDays(to, 30);
  return { from, to };
}

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    maximumFractionDigits: currency === 'UZS' ? 0 : 2,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('ru-RU').format(value);
}

export function AnalyticsTab() {
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultRange);

  const payload = useMemo(() => {
    if (!dateRange.from || !dateRange.to) {
      return null;
    }
    return {
      startDate: dateRange.from.toISOString(),
      endDate: dateRange.to.toISOString(),
    };
  }, [dateRange]);

  const { query } = useCustom<PaymentAnalyticsResponse>({
    url: '/payments/analytics',
    method: 'post',
    config: {
      payload: payload ?? {},
    },
    queryOptions: {
      enabled: payload !== null,
      retry: false,
    },
    errorNotification: (error) => ({
      message: error?.message ?? 'Не удалось загрузить аналитику',
      type: 'error',
    }),
  });

  const data = query.data?.data;
  const isLoading = query.isLoading || query.isFetching;
  const isError = query.isError;

  const handleRetry = () => {
    void query.refetch();
    notify.info('Обновляем аналитику…');
  };

  return (
    <div className="space-y-4 pt-4">
      {/* Date range */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex-1 max-w-md">
          <DateRangePicker
            value={dateRange.from && dateRange.to ? { from: dateRange.from, to: dateRange.to } : null}
            onChange={(range) => {
              if (range?.from && range.to) {
                setDateRange({ from: range.from, to: range.to });
              }
            }}
          />
        </div>
        <Button variant="outline" size="sm" onClick={handleRetry} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Обновить
        </Button>
      </div>

      {/* Error state */}
      {isError && (
        <Card className="border-destructive/50">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">Ошибка загрузки аналитики</p>
              <p className="text-xs text-muted-foreground">
                Проверьте соединение и попробуйте ещё раз.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleRetry}>
              Повторить
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading skeleton */}
      {isLoading && !data && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {['rev', 'conf', 'pend', 'ref', 'conv'].map((k) => (
            <Skeleton key={k} className="h-24 rounded-xl" />
          ))}
        </div>
      )}

      {/* Summary cards */}
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <SummaryCard
              icon={<TrendingUp className="h-4 w-4" />}
              label="Выручка"
              value={formatCurrency(data.summary.totalRevenueBase, data.summary.baseCurrency)}
              hint={`${formatNumber(data.summary.confirmedCount)} оплат`}
              accent="text-green-600 dark:text-green-500"
            />
            <SummaryCard
              icon={<CreditCard className="h-4 w-4" />}
              label="Подтверждено"
              value={formatNumber(data.summary.confirmedCount)}
              hint={`Средний чек ${formatCurrency(data.summary.averageCheck, data.summary.baseCurrency)}`}
            />
            <SummaryCard
              icon={<Clock className="h-4 w-4" />}
              label="Ожидают"
              value={formatNumber(data.summary.pendingCount)}
              hint={`${formatNumber(data.summary.rejectedCount)} отклонено`}
              accent="text-yellow-600 dark:text-yellow-500"
            />
            <SummaryCard
              icon={<RefreshCw className="h-4 w-4" />}
              label="Возвраты"
              value={formatNumber(data.summary.refundedCount)}
              hint="За период"
            />
            <SummaryCard
              icon={<Percent className="h-4 w-4" />}
              label="Конверсия"
              value={`${(data.summary.conversionRate * 100).toFixed(1)}%`}
              hint="confirmed / total"
              accent="text-blue-600 dark:text-blue-500"
            />
          </div>

          {/* Revenue by day chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Выручка по дням</CardTitle>
            </CardHeader>
            <CardContent>
              {data.revenueByDay.length === 0 ? (
                <div className="flex items-center justify-center h-[240px] text-sm text-muted-foreground">
                  Нет подтверждённых платежей за выбранный период
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart
                    data={data.revenueByDay.map((d) => ({
                      ...d,
                      formattedDate: format(parseISO(d.date), 'd MMM', { locale: ru }),
                    }))}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="paymentRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="formattedDate"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(v) =>
                        new Intl.NumberFormat('ru-RU', {
                          notation: 'compact',
                          compactDisplay: 'short',
                        }).format(v)
                      }
                      width={60}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: '1px solid hsl(var(--border))',
                        background: 'hsl(var(--popover))',
                        fontSize: 12,
                      }}
                      formatter={(value) => [formatNumber(typeof value === 'number' ? value : 0), 'Выручка']}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#22c55e"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#paymentRevenue)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Two-column row: provider pie + top targets table */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">По провайдерам</CardTitle>
              </CardHeader>
              <CardContent>
                {data.byProvider.length === 0 ? (
                  <div className="flex items-center justify-center h-[240px] text-sm text-muted-foreground">
                    Нет данных
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={data.byProvider.map((p) => ({
                          name: PROVIDER_LABELS[p.provider] ?? p.provider,
                          value: p.revenue,
                          provider: p.provider,
                          count: p.count,
                        }))}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                      >
                        {data.byProvider.map((p) => (
                          <Cell
                            key={p.provider}
                            fill={PROVIDER_COLORS[p.provider] ?? '#6b7280'}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          borderRadius: 8,
                          border: '1px solid hsl(var(--border))',
                          background: 'hsl(var(--popover))',
                          fontSize: 12,
                        }}
                        formatter={(value) => formatNumber(typeof value === 'number' ? value : 0)}
                      />
                      <Legend
                        verticalAlign="bottom"
                        iconType="circle"
                        wrapperStyle={{ fontSize: 12 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Топ продуктов (по выручке)</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {data.topTargets.length === 0 ? (
                  <div className="flex items-center justify-center h-[240px] text-sm text-muted-foreground">
                    Нет данных
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Тип</TableHead>
                        <TableHead className="text-right">Оплат</TableHead>
                        <TableHead className="text-right">Выручка</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.topTargets.map((t) => (
                        <TableRow key={t.targetType}>
                          <TableCell className="font-medium">
                            {TARGET_TYPE_LABELS[t.targetType] ?? t.targetType}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatNumber(t.count)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-medium">
                            {formatNumber(t.revenue)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

interface SummaryCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint: string;
  accent?: string;
}

function SummaryCard({ icon, label, value, hint, accent }: SummaryCardProps) {
  return (
    <div className="rounded-xl border bg-card p-4 flex items-start gap-3">
      <div className="text-muted-foreground shrink-0 mt-0.5">{icon}</div>
      <div className="min-w-0 flex-1">
        <p className={`text-xl font-semibold truncate ${accent ?? ''}`}>{value}</p>
        <p className="text-[11px] text-muted-foreground truncate">{label}</p>
        <p className="text-[10px] text-muted-foreground truncate mt-0.5">{hint}</p>
      </div>
    </div>
  );
}
